'use client'

import Link from 'next/link'
import { usePathname } from 'next/navigation'
import { useCallback, useMemo, useState } from 'react'
import { useBoot } from '@/app/(app)/context/BootContext'
import { SigmaBrandMark } from '@/components/SigmaBrandMark'
import { useSigmaSurface } from '@/components/SigmaSurfaceProvider'

type NavItem = {
  label: string
  href: string
  relHome?: boolean
  /** Default query params so the page opens directly with this state (e.g. page=1, status=all). */
  defaultQuery?: Record<string, string>
}

/**
 * Shopify embedded app navigation.
 * Links use path-only URLs (no shop/host in query) so the address bar stays clean.
 * Page-specific default params (e.g. page=1, status=all) are still applied.
 *
 * Top-level structure (6 items):
 *   Dashboard · Orders · Customers · Inventory · Expenses · Integrations · Settings
 *
 * Routes removed from nav but still accessible directly:
 *   /get-started, /analytics, /analytics/couriers, /analytics/areas,
 *   /action-center, /products, /sku-costs, /sku-expenses,
 *   /customer-risk, /address-review, /billing, /admin, /contact,
 *   /reports, /calls, /replacements, /settlements, /metric-breakdown
 */
const NAV_ITEMS: NavItem[] = [
  { label: 'Dashboard', href: '/dashboard', relHome: true },
  { label: 'Orders', href: '/orders', defaultQuery: { page: '1' } },
  { label: 'Customers', href: '/customers', defaultQuery: { page: '1' } },
  { label: 'Inventory', href: '/inventory', defaultQuery: { tab: 'delivered' } },
  { label: 'Expenses', href: '/expenses' },
  { label: 'Integrations', href: '/integrations' },
  { label: 'Settings', href: '/settings' },
]

export function AppNav() {
  const pathname = usePathname() ?? ''
  const { bootShop: shop } = useBoot()
  const surface = useSigmaSurface()
  const [signingOut, setSigningOut] = useState(false)

  const buildHref = useMemo(() => {
    return (item: NavItem) => {
      if (!item.defaultQuery || Object.keys(item.defaultQuery).length === 0) return item.href
      const qs = new URLSearchParams()
      for (const [k, v] of Object.entries(item.defaultQuery)) {
        qs.set(k, v)
      }
      return `${item.href}?${qs.toString()}`
    }
  }, [])

  const onLogout = useCallback(async () => {
    setSigningOut(true)
    try {
      await fetch('/api/portal/logout', { method: 'POST', credentials: 'include' })
    } catch {
      // still navigate away
    }
    window.localStorage.removeItem('shopify-shop')
    window.localStorage.removeItem('shopify-host')
    window.location.href = `${window.location.origin.replace(/\/$/, '')}/login`
  }, [])

  if (!shop) return null

  const links = NAV_ITEMS.map((item) => {
    const href = buildHref(item)
    const pathOnly = item.href
    const active =
      pathname === pathOnly || (pathOnly !== '/' && pathname.startsWith(`${pathOnly}/`))
    return (
      <Link
        key={item.href}
        href={href}
        prefetch={false}
        className={surface === 'portal' ? (active ? 'sigma-portal-nav__link sigma-portal-nav__link--active' : 'sigma-portal-nav__link') : undefined}
        {...(item.relHome ? { rel: 'home' } : {})}
      >
        {item.label}
      </Link>
    )
  })

  /* Portal: App Bridge script is off — premium shell replaces <s-app-nav>. */
  if (surface === 'portal') {
    return (
      <header className="sigma-portal-nav" role="banner">
        <div className="sigma-portal-nav__inner">
          <div className="sigma-portal-nav__bar">
            <Link href="/dashboard" className="sigma-portal-nav__brand" prefetch={false} aria-label="SigmaSense home">
              <SigmaBrandMark size={34} priority alt="" />
              <span className="sigma-portal-nav__wordmark">SigmaSense</span>
            </Link>
            <div className="sigma-portal-nav__actions">
              <Link href="/select-store?switch=1" className="sigma-portal-nav__ghost" prefetch={false}>
                Switch store
              </Link>
              <button
                type="button"
                className="sigma-portal-nav__ghost sigma-portal-nav__ghost--signout"
                disabled={signingOut}
                onClick={() => void onLogout()}
              >
                {signingOut ? 'Signing out…' : 'Sign out'}
              </button>
            </div>
          </div>
          <nav className="sigma-portal-nav__items" aria-label="Main navigation">
            {links}
          </nav>
        </div>
      </header>
    )
  }

  return <s-app-nav>{links}</s-app-nav>
}
